// Canonical certificate paths introduced by #2325 and every provider-specific path
// they replace. Kept outside the catalogues so migration tooling and compatibility
// readers share one authoritative map.

const SHARED_QUALIFICATION_PATHS = {
    'department.sogs': ['truckReady.sogs'],
    'provincial.whmis': ['truckReady.whmis'],
    'provincial.ics100': ['exterior.ics100', 'truckReady.ics100'],
    'provincial.trafficControl': ['exterior.trafficControl', 'truckReady.trafficControl'],
    'provincial.gasSafety': ['truckReady.gasSafety'],
    'provincial.electricalSafety': ['truckReady.electricalSafety'],
    'provincial.hazmatAwareness': ['exterior.hazmatAwareness', 'truckReady.hazmatAwareness'],
    'provincial.sppWff1': ['exterior.sppWff1'],
    'provincial.firstAid': ['exterior.fr3', 'exterior.firstAid', 'truckReady.firstAid'],
    'provincial.apparatusDriverOperator': ['exterior.apparatusDriverOperator', 'truckReady.apparatusDriverOperator'],
    'shared.hazmatOps': ['fullService.hazmatOps', 'fullService.nfpa1072Hazmat', 'ff2.hazmatOps']
};

const LEGACY_QUALIFICATION_PATHS = Object.fromEntries(
    Object.entries(SHARED_QUALIFICATION_PATHS)
        .flatMap(([canonical, legacyPaths]) => legacyPaths.map(path => [path, canonical]))
);

// The provider-native paths used before the canonical categories existed. During the
// deploy-before-migrate window a member can still have cells from two providers. Reads
// and writes must follow the active provider's cell or a VIERA department could display
// and update an older JIBC record immediately before migration.
const PROVIDER_LEGACY_QUALIFICATION_PATHS = {
    jibc: {
        'provincial.ics100': 'exterior.ics100',
        'provincial.trafficControl': 'exterior.trafficControl',
        'provincial.hazmatAwareness': 'exterior.hazmatAwareness',
        'provincial.sppWff1': 'exterior.sppWff1',
        'provincial.firstAid': 'exterior.fr3',
        'provincial.apparatusDriverOperator': 'exterior.apparatusDriverOperator',
        'shared.hazmatOps': 'fullService.hazmatOps'
    },
    viera: {
        'department.sogs': 'truckReady.sogs',
        'provincial.whmis': 'truckReady.whmis',
        'provincial.ics100': 'exterior.ics100',
        'provincial.trafficControl': 'truckReady.trafficControl',
        'provincial.gasSafety': 'truckReady.gasSafety',
        'provincial.electricalSafety': 'truckReady.electricalSafety',
        'provincial.hazmatAwareness': 'truckReady.hazmatAwareness',
        'provincial.sppWff1': 'exterior.sppWff1',
        'provincial.firstAid': 'exterior.fr3',
        'provincial.apparatusDriverOperator': 'truckReady.apparatusDriverOperator',
        'shared.hazmatOps': 'fullService.nfpa1072Hazmat'
    },
    nfpa1010: {
        'provincial.whmis': 'truckReady.whmis',
        'provincial.ics100': 'truckReady.ics100',
        'provincial.trafficControl': 'truckReady.trafficControl',
        'provincial.gasSafety': 'truckReady.gasSafety',
        'provincial.electricalSafety': 'truckReady.electricalSafety',
        'provincial.hazmatAwareness': 'truckReady.hazmatAwareness',
        'provincial.firstAid': 'exterior.fr3',
        'provincial.apparatusDriverOperator': 'truckReady.apparatusDriverOperator',
        'shared.hazmatOps': 'ff2.hazmatOps'
    }
};

PROVIDER_LEGACY_QUALIFICATION_PATHS.vieraGeneric = PROVIDER_LEGACY_QUALIFICATION_PATHS.viera;

const preferredLegacyQualificationPath = (trainingProvider, canonical) =>
    PROVIDER_LEGACY_QUALIFICATION_PATHS[trainingProvider]?.[canonical] || null;

const valueAtPath = (skills, path) => {
    const [level, key] = String(path || '').split('.');
    return level && key ? skills?.[level]?.[key] : undefined;
};

const canonicalQualificationPath = path => LEGACY_QUALIFICATION_PATHS[path] || path;

const canonicalQualificationGrouping = grouping => typeof grouping === 'string' && grouping.startsWith('skills.')
    ? `skills.${canonicalQualificationPath(grouping.slice('skills.'.length))}`
    : grouping;

const withCanonicalQualificationFiles = files => Array.isArray(files)
    ? files.map(file => {
        const grouping = canonicalQualificationGrouping(file?.grouping);
        return grouping === file?.grouping ? file : {...file, grouping};
    })
    : files;

// During the rollout, code reads the canonical cell first and falls back to an old
// provider path. The migration refuses conflicts, so choosing the first legacy value is
// only relevant before migration, when a tenant has records under one provider path.
const qualificationCellAt = (skills, path, trainingProvider) => {
    const canonical = canonicalQualificationPath(path);
    const current = valueAtPath(skills, canonical);
    // A canonical value is authoritative, including null and fields deliberately
    // removed from an object. Combining it with a legacy cell resurrects cleared
    // values and destroys Date/Timestamp prototypes by spreading them as maps.
    if (current !== undefined) return current;

    const legacyPaths = SHARED_QUALIFICATION_PATHS[canonical] || [];
    const preferred = preferredLegacyQualificationPath(trainingProvider, canonical);
    for (const legacy of preferred ? [preferred, ...legacyPaths.filter(path => path !== preferred)] : legacyPaths) {
        const value = valueAtPath(skills, legacy);
        if (value !== undefined) return value;
    }
    return undefined;
};

// Some catalogues are views over part of a stored cell. COTR, for example, splits a
// J&B chapter's drills between Exterior and Interior while deliberately keeping the
// underlying sign-offs in the one provider-neutral `ff1.*` cell. A matrix column must
// therefore describe the drills assigned to that column, not the whole stored chapter.
// `progressDrills` is display metadata only; writes still target `path`.
const qualificationDisplayCellAt = (skills, path, definition, trainingProvider) => {
    const cell = qualificationCellAt(skills, path, trainingProvider);
    const progressDrills = trainingProvider === 'cotr' && Array.isArray(definition?.progressDrills)
        ? definition.progressDrills
        : null;
    if (!progressDrills?.length || !cell || typeof cell !== 'object') return cell;

    const drills = cell.practical?.drills || {};
    const completed = progressDrills.filter(code => drills[code]?.done).length;
    const touched = progressDrills.some(code => Object.prototype.hasOwnProperty.call(drills, code));
    const storedStatus = cell.status;
    const status = completed === progressDrills.length
        ? 'COMPLETE'
        : (touched ? 'INPROGRESS' : (['NEEDED', 'SCHEDULED'].includes(storedStatus) ? storedStatus : 'INCOMPLETE'));
    const {theory: _theory, practical: _practical, ...metadata} = cell;
    return {...metadata, status};
};

const qualificationCellSourcePath = (skills, path, trainingProvider) => {
    const canonical = canonicalQualificationPath(path);
    if (valueAtPath(skills, canonical) !== undefined) return canonical;
    const preferred = preferredLegacyQualificationPath(trainingProvider, canonical);
    if (preferred && valueAtPath(skills, preferred) !== undefined) return preferred;
    return (SHARED_QUALIFICATION_PATHS[canonical] || []).find(legacy => valueAtPath(skills, legacy) !== undefined) || canonical;
};

// Materialise canonical paths in an in-memory skills map without mutating the Firestore
// document. This is the deploy-before-migrate compatibility layer: every existing
// renderer and calculation can read the new path. Writers use
// qualificationCellSourcePath so an existing legacy cell remains single-sourced until
// the migration moves it; records created after migration use the canonical path.
const withSharedQualificationFallbacks = (skills, trainingProvider) => {
    if (!skills || typeof skills !== 'object') return skills;
    let result = skills;

    for (const canonical of Object.keys(SHARED_QUALIFICATION_PATHS)) {
        const [level, key] = canonical.split('.');
        if (valueAtPath(result, canonical) !== undefined) continue;
        const value = qualificationCellAt(result, canonical, trainingProvider);
        if (value === undefined) continue;
        if (result === skills) result = {...skills};
        result[level] = {...result[level], [key]: value};
    }

    return result;
};

module.exports = {
    SHARED_QUALIFICATION_PATHS,
    LEGACY_QUALIFICATION_PATHS,
    PROVIDER_LEGACY_QUALIFICATION_PATHS,
    preferredLegacyQualificationPath,
    canonicalQualificationPath,
    canonicalQualificationGrouping,
    withCanonicalQualificationFiles,
    qualificationCellAt,
    qualificationDisplayCellAt,
    qualificationCellSourcePath,
    withSharedQualificationFallbacks,
    valueAtPath
};
